import {
  useState,
  useRef,
  Children,
  forwardRef,
  ReactNode,
  ReactElement,
} from "react";
import styled, { keyframes } from "styled-components/macro";
import {
  Table as VirtualizedTable,
  AutoSizer,
  InfiniteLoader,
  Column as VColumn,
  ColumnProps as VColumnProps,
  TableHeaderRenderer,
} from "react-virtualized";
import "react-virtualized/styles.css";
import { THEME_COLOR, BORDER_RADIUS, THEME_COLOR_DARK } from "../vars";
import usePages, { PaginatingEndpoint } from "../hooks/use-pages";
import { useSkipioApi } from "../api";
import { ChevronDown, ChevronUp } from "./Icons";
import { transformKey, caseMethods } from "../api/case";
import Checkbox from "./Checkbox";
import classNames from "classnames";
import { UrlBuilder } from "@synvox/api";

type ColumnProps = Omit<VColumnProps, "width"> & {
  width: number | string;
};

const Header: TableHeaderRenderer = function Header({
  label,
  sortBy,
  sortDirection,
  dataKey,
}: {
  label?: ReactNode;
  sortBy?: string;
  sortDirection?: "ASC" | "DESC";
  dataKey?: string;
}) {
  const style = { marginLeft: "8px", width: "16px", height: "16px" };
  return (
    <>
      {label}{" "}
      {sortBy === dataKey &&
        (sortDirection === "ASC" ? (
          <ChevronDown style={style} />
        ) : (
          <ChevronUp style={style} />
        ))}
    </>
  );
};

function filterObjectValues(obj: Record<string, any>) {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v));
}

function Table({
  endpoint,
  params = {},
  children,
  sortByPrefix = "",
  onRowClick,
  isSelected = () => false,
  defaultSortBy,
  defaultSortOrder = "ASC",
  ...props
}: {
  endpoint: ({
    skipioApi,
  }: {
    skipioApi: UrlBuilder<any>;
  }) => PaginatingEndpoint<any>;
  params?: Record<string, any>;
  children?: ReactNode;
  sortByPrefix?: string;
  onRowClick?: (row: any) => void;
  isSelected?: (row: any) => boolean;
  defaultSortBy?: string;
  defaultSortOrder?: "ASC" | "DESC";
}) {
  const skipioApi = useSkipioApi();
  const tableRef = useRef<VirtualizedTable>(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(defaultSortBy);
  const [sortDirection, setSortDirection] = useState(defaultSortOrder);
  const { data, loading, totalCount } = usePages(
    endpoint({ skipioApi }),
    filterObjectValues({
      ...params,
      sort: !sortBy ? undefined : transformKey(sortBy, caseMethods.snake),
      order: sortDirection.toLowerCase(),
    }),
    page
  );

  const sort = ({
    sortBy,
    sortDirection,
  }: {
    sortBy: string;
    sortDirection: "ASC" | "DESC";
  }) => {
    setPage(1);
    setSortBy(sortBy);
    setSortDirection(sortDirection);
    tableRef.current!.scrollToPosition(0);
  };

  const filteredChildren: ReactElement<ColumnProps>[] = Children.toArray(
    children
  ).filter(Boolean) as any[];

  const fixedWidthColumnSize = filteredChildren
    .map((node) =>
      typeof node.props.width === "string"
        ? Number(node.props.width.replace("px", ""))
        : 0
    )
    .reduce((a, b) => a + b, 0);

  const totalColumns = filteredChildren
    .map((node) =>
      typeof node.props.width === "string" ? 0 : node.props.width
    )
    .reduce((a, b) => a + b, 0);

  return (
    <Container>
      <div
        style={{
          position: "absolute",
          bottom: "40px",
          right: "40px",
          zIndex: 100,
        }}
      >
        <LoaderCircle style={{ opacity: loading ? 1 : 0 }} />
      </div>
      <AutoSizer>
        {({ width, height }) => (
          <InfiniteLoader
            isRowLoaded={({ index }) => Boolean(data[index])}
            loadMoreRows={async () => {
              if (loading) return;
              setPage(page + 1);
            }}
            rowCount={totalCount}
          >
            {({ onRowsRendered, registerChild }) => (
              <VirtualizedTable
                onRowClick={
                  onRowClick ? (p) => onRowClick({ ...p, data }) : undefined
                }
                ref={tableRef}
                width={width || 640}
                height={height || 480}
                onRowsRendered={onRowsRendered}
                registerChild={registerChild}
                sort={sort}
                sortBy={sortBy}
                sortDirection={sortDirection}
                headerHeight={40}
                rowHeight={50}
                rowCount={data.length}
                rowGetter={({ index }) => data[index]}
                overscanRowCount={40}
                rowClassName={({ index }) =>
                  classNames(
                    index < 0 ? undefined : index % 2 === 0 ? "even" : "odd",
                    {
                      selected: index >= 0 && isSelected(data[index]),
                    }
                  )
                }
                {...props}
              >
                {filteredChildren.map((node, index) => (
                  <VColumn
                    {...{
                      key: index,
                      disableSort: true,
                      headerRenderer: Header,
                      ...node.props,
                      width:
                        typeof node.props.width === "string"
                          ? Number(node.props.width.replace("px", ""))
                          : ((width - fixedWidthColumnSize) / totalColumns) *
                              node.props.width || 0,
                    }}
                  />
                ))}
              </VirtualizedTable>
            )}
          </InfiniteLoader>
        )}
      </AutoSizer>
    </Container>
  );
}

export const Column = forwardRef<VColumn, ColumnProps>(function Column() {
  throw new Error("<Column /> must be an immediate child of <Table />");
});

const Container = styled.div`
  width: 100%;
  height: 100%;
  padding: 0px;
  position: relative;
  .ReactVirtualized__Table {
    position: absolute;
    box-shadow: 0px 0px 0px 1px #dcdcdc inset;
    overflow: hidden;
    border-radius: ${BORDER_RADIUS};
    background: white;
  }
  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    display: flex;
    align-items: center;

    padding: 0px 10px;
    &:not(:first-child) {
      border-left: 1px solid rgba(0, 0, 0, 0.05);
    }
  }
  .ReactVirtualized__Table__headerRow {
    background: ${THEME_COLOR};
    box-shadow: 0 0 0 1px ${THEME_COLOR_DARK} inset;
    color: white;
    text-transform: none;
    font-weight: 500;
  }
  .ReactVirtualized__Table__row {
    cursor: pointer;
    box-shadow: 0px 0px 0px 1px transparent;
    transition: background 0.2s;
    user-select: none;
    &.even {
    }
    &.odd {
      background: rgba(0, 0, 0, 0.01);
    }
    &.selected {
      background: ${THEME_COLOR}11;
    }
    &:hover:not(.selected) {
      background: rgba(0, 0, 0, 0.04);
    }
    &:active:not(.selected) {
      background: rgba(0, 0, 0, 0.06);
    }
  }
  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    display: flex;
    align-items: center;
    white-space: normal;
    padding: 0px 10px;
    &:not(:first-child) {
      border-left: 1px solid rgba(0, 0, 0, 0.05);
    }
  }
  .ReactVirtualized__Table__sortableHeaderColumn {
  }
  .ReactVirtualized__Table__sortableHeaderIcon {
  }
  ${Checkbox} {
    top: -10px;
  }
`;

const loaderKeyframes = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const LoaderCircle = styled.div`
  display: block;
  border-radius: 10000px;
  height: 40px;
  width: 40px;
  border: 4px solid transparent;
  border-left-color: ${THEME_COLOR};
  animation: 1s ${loaderKeyframes} linear infinite;
  transition-duration: 0.2s;
`;

export default Table;
